import type { BoardLane, BoardTask } from '../api/work'
import { bestFitFor, canReceiveWork, sumCredits, type FloorAwareLane } from './laneWorkload'

/* ============================================================================
   **W-42 · THE SYSTEM PROPOSES AND THE SUPERVISOR APPROVES.** Sprint 73.
   **A FLOOR SOMEBODY IS WORKING IS HIS — CQ-145, ANSWERED 19 SEPTEMBER.** Sprint 81.

   The client's answer to "automatic allocation", in their own words: *"System proposes,
   supervisor approves."* Not *"the system allocates and the supervisor may undo it"* —
   which is a different product, and the difference is who is accountable for a morning
   that goes wrong.

   So **nothing here moves anything.** It returns a plan. The board draws it as a proposal,
   the supervisor reads it, and it becomes real only when he says so, through the same
   per-task assignment the board has always used. There is no second way to assign a room.

   ── WHY THE BOARD-WIDE PROBLEM IS NOT `bestFitFor` IN A LOOP ────────────────────────
   `bestFitFor` answers *"who should take THIS room"* against a board as it stands, and it
   is right. Asked twenty times about twenty rooms it is wrong twenty times in the same
   direction: every answer is computed against the ORIGINAL workloads, so the man who
   starts with the most room is recommended for all twenty and ends the exercise four hours
   over his shift.

   **The running total is the whole difference.** Each placement is charged to the lane
   before the next room is asked about, which is what makes the result a plan rather than
   twenty independent opinions.

   ── IT PLACES THE HEAVIEST ROOMS FIRST, AND THAT IS NOT AN OPTIMISATION ─────────────
   Taking the cards in the order the board happens to send them fills the last lane with
   whatever is left, and what is left is whatever nobody could fit — so the one room that
   needed the biggest gap is the one offered last, to whoever has the smallest. Heaviest
   first is the ordinary way round: the thing that is hardest to place is placed while
   there is still somewhere to put it.

   ── AND WHAT IT CANNOT PLACE IS NAMED, NOT DROPPED ──────────────────────────────────
   A proposal that quietly covers eleven of fifteen rooms and says "11 rooms" is a
   proposal a supervisor approves and then discovers. Every unplaced room comes back with
   the reason, and the screen says so before he approves anything.

   ── CQ-145: A FLOOR SOMEBODY IS WORKING IS THAT MAN'S FLOOR ─────────────────────────
   Until Sprint 81 the floor was the FIRST comparison in a ranking that still considered
   everybody: a man two floors away with four credits spare was proposed the room as soon
   as the floor's own man had none. The client closed that door in one sentence:
   ***"others will not have access to non allocated floors"*** — chosen alongside the
   option that reads *"floor first, until a lane is full, then spill to the lightest"*.

   **THE TWO SENTENCES ARE ONE RULE, AND THE JOINT IS THE WORD "ALLOCATED".**

   A floor somebody was ALREADY WORKING when the plan was asked for is allocated to him.
   Its rooms are offered to him and to anyone else already working it, they spill between
   those men when one reaches his hours, and when every one of them is full the room comes
   back UNPLACED. It is not quietly handed to the freest man on the board, which is exactly
   what the old ranking did and exactly what the client's sentence forbids.

   A floor NOBODY was working is allocated to nobody, and the second sentence is what
   governs it: it is offered to the whole shift, the first placement makes it his so the
   rest of the floor follows him, and when he reaches his hours it spills to the lightest.
   **WITHOUT THIS HALF THE PRODUCT STOPS WORKING ON THE COMMONEST MORNING THERE IS** — at
   seven o'clock every lane is empty, no floor is allocated to anybody, and a rule that
   demanded a holder would propose nothing at all, every day, for ever.

   So the wall is built out of THE MORNING'S allocation and never out of the plan's own.
   A man the plan put on an empty floor five rooms ago was not allocated that floor by
   anybody; preferring him is right, walling four idle attendants out behind him is a
   decision this function has no standing to take.

   ── WHAT THIS GOVERNS, AND WHAT IT DOES NOT ─────────────────────────────────────────
   **THE SUGGESTION ONLY.** Nothing in the assignment service refuses a supervisor who
   drags a room to somebody on another floor, and Sprint 81 deliberately did not add one:
   the client's answer was given about who the system should PROPOSE work to, and turning
   it into a refusal would stop a supervisor covering a man who went home sick. If it is
   meant as a hard rule it belongs in `AssignmentService` as a refusal with its own
   confirmation, and that question has not been asked yet.
   ========================================================================= */

/** Why a room is not in the plan. Four different sentences, and four different acts. */
export type UnplacedReason =
  /** The card itself cannot be handed to anybody — #116's unconfigured room. */
  | 'NOT_ASSIGNABLE'
  /** Nobody on shift has room left. The supervisor has to overrule or change the shift. */
  | 'NOBODY_FREE'
  /**
   * **THE MEN WORKING THAT FLOOR ARE AT THEIR HOURS AND SOMEBODY ELSE IS NOT.**
   *
   * A different sentence from `NOBODY_FREE` and a different act. `NOBODY_FREE` is a
   * morning that needs more hands; this is a ROSTER — there is capacity on this board and
   * CQ-145 says it may not be offered rooms on a floor it is not working, so the fix is to
   * put a hand on that floor, or to drag the card across, which nothing stops a supervisor
   * doing. Telling him "everybody on shift is at their hours" while he can see two
   * half-empty lanes is the screen lying to him about the one thing he can act on.
   *
   * **IT IS ONLY USED WHERE THE DISTINCTION IS REAL.** A floor's men being full on a board
   * where nobody has an hour left is `NOBODY_FREE`, because that is the true sentence.
   */
  | 'NOBODY_FREE_ON_FLOOR'
  /** Nobody is on shift at all. A different problem, and a different person's. */
  | 'NOBODY_ON_SHIFT'

export interface ProposedPlacement {
  readonly task: BoardTask
  readonly toUserId: string
  /** The lane's name as the board draws it, so the proposal can be read without a lookup. */
  readonly toLabel: string
  readonly toLabelAr: string | null
  /**
   * **TRUE WHEN THE PERSON WAS ALREADY WORKING THAT FLOOR.**
   *
   * CQ-141 put the floor first and CQ-145 made it a permission, so since Sprint 81 this
   * can only be false in one situation: **the floor was allocated to nobody** — either
   * this placement opened it, or it opened for somebody else and spilled here. Those are
   * the lines of a plan a supervisor may actually want to move, and they are exactly the
   * lines that are still his to move: nothing was taken off anybody's floor to make them.
   */
  readonly onThisFloor: boolean
}

export interface UnplacedRoom {
  readonly task: BoardTask
  readonly reason: UnplacedReason
}

export interface AllocationProposal {
  readonly placements: readonly ProposedPlacement[]
  readonly unplaced: readonly UnplacedRoom[]
  /**
   * **WHO WOULD BE LEFT WITH NOTHING TO DO IF THIS PLAN WERE APPROVED.**
   *
   * ── IT IS NOW A CONSEQUENCE OF THE RULE, NOT A SYMPTOM OF ONE ─────────────────────
   * It was written in Sprint 73 as the honest report of an open question: CQ-141 put the
   * floor first, a board-wide application of it stacked one man to 10.75 of 18 while two
   * attendants held nothing, and bounding the preference would have meant inventing a
   * threshold for what a walk costs. **CQ-145 has since been answered, and the answer went
   * the other way**: the floor is a wall, so a quiet floor CANNOT be shared out.
   *
   * That makes this figure more important rather than less. A man idle under the old rule
   * was a plan that could have been better; a man idle under this one is a ROSTER that
   * needs a hand moved to a floor, which is a thing the supervisor does before the morning
   * rather than an imbalance he shrugs at. It is still reported and still not corrected —
   * correcting it would mean breaking the wall the client just built.
   */
  readonly wouldBeIdle: readonly IdleLane[]
}

/** Somebody on shift who would hold nothing once this plan was applied. */
export interface IdleLane {
  readonly userId: string
  readonly label: string
  readonly labelAr: string | null
}

/** The floors a lane is already working, from the cards it holds. */
const floorsOf = (lane: BoardLane): Set<string> =>
  new Set(lane.tasks.map((t) => t.floorCode))

/**
 * **A ROOM NOBODY CAN BE GIVEN IS NOT A ROOM TO ALLOCATE.**
 *
 * #116 again: the sweep raises a card for a room with no cleaning rules on purpose,
 * because the card is the discovery. Proposing it to somebody would turn a discovery into
 * a refusal at the moment of approval, which is the one moment a supervisor is not looking
 * for one.
 */
const isAssignable = (task: BoardTask): boolean =>
  task.missingCleaningRules.length === 0

/**
 * **HAS HE ROOM FOR THIS PARTICULAR ROOM — DEFECT #179, Sprint 81.**
 *
 * `bestFitFor` asks *"has he any room at all"*, which is the right question for M-14: a
 * supervisor holding one card is choosing the best of the people in front of him, and a
 * man half a credit short is still the answer. A PLAN is a different question, because a
 * plan is charged: eight rooms proposed to a man with half a credit spare are eight rooms
 * he has been given.
 *
 * The flaw was latent for eight sprints because every test in this file but one used
 * one-credit rooms, where "any room at all" and "room for this one" are the same sentence.
 * A 2-credit room offered to a man at 7.5 of 8 put him at 9.5 — the screen's own headline
 * rule, *never propose more than a man's shift*, broken by the code that states it.
 *
 * **IT IS NOT FIXED INSIDE `bestFitFor`**, which has no room to compare against and would
 * have to be told about one. The plan knows the room; the plan does the arithmetic.
 *
 * In hundredths, because credits are DECIMAL(4,2) on the wire and 7.5 + 2 is 9.5 in
 * decimal and 9.500000000000002 on a bad day in binary — `sumCredits`' own reasoning.
 */
const hasRoomFor = (budget: number, load: number, credits: number): boolean =>
  Math.round(budget * 100) - Math.round(load * 100) >= Math.round(credits * 100)

export function proposeAllocation(
  unassigned: readonly BoardTask[],
  lanes: readonly BoardLane[],
): AllocationProposal {
  const placements: ProposedPlacement[] = []
  const unplaced: UnplacedRoom[] = []

  const onShift = lanes.filter(
    (l) => canReceiveWork(l.rosterState) && l.creditBudget !== null)

  /* **THE RUNNING STATE, AND IT IS THE POINT OF THIS FUNCTION.** A lane's load starts as
     the sum of the cards it already holds and grows with every placement — so the next
     room is asked about a board that reflects the plan so far rather than the morning.
     Its `floors` are the PREFERENCE — where the plan has already sent this man — and are
     what keeps a floor together. What he is ALLOWED to be offered is `allocated`, below,
     and the two are deliberately different sets. */
  const held = new Map<string, { extra: number; floors: Set<string> }>(
    onShift.map((l) => [l.userId, { extra: 0, floors: floorsOf(l) }]))

  /* **THE MORNING'S ALLOCATION, TAKEN ONCE AND NEVER ADDED TO — CQ-145's WALL.**
     Who is working which floor, read from the cards the lanes were holding when the plan
     was asked for. It is deliberately NOT the same set as `held.floors`, which grows with
     every placement: `held.floors` is a preference and this is a permission. */
  const allocated = new Map<string, Set<string>>()
  for (const lane of onShift)
    for (const floor of floorsOf(lane))
      (allocated.get(floor) ?? allocated.set(floor, new Set()).get(floor)!).add(lane.userId)

  /* HEAVIEST FIRST, with the room number as the tie-break so the plan is the same plan
     every time it is asked for. A proposal that reshuffles between two presses is one a
     supervisor stops reading. */
  const queue = [...unassigned].sort(
    (a, b) => b.credits - a.credits
      || a.roomNumber.localeCompare(b.roomNumber, 'en', { numeric: true }))

  for (const task of queue) {
    if (!isAssignable(task)) {
      unplaced.push({ task, reason: 'NOT_ASSIGNABLE' })
      continue
    }

    if (onShift.length === 0) {
      unplaced.push({ task, reason: 'NOBODY_ON_SHIFT' })
      continue
    }

    /* ── CQ-145 · WHO IS EVEN ALLOWED TO BE ASKED ────────────────────────────────
       **THE WALL IS BUILT HERE, BEFORE THE RANKING AND NOT INSIDE IT.** The old code
       handed every lane on the board to `bestFitFor` and relied on the floor being its
       first comparison — which means a lane off the floor was always a legal answer and
       became THE answer the moment the floor's own man filled up. A rule that says
       *"others will not have access"* cannot be written as an ordering, because an
       ordering is a preference. So the candidates are cut down first, and the ranking then
       chooses among men who are all entitled to the room.

       **`allocated`, NOT `held`.** The morning's allocation is the wall; the plan's own
       placements are not. See the header — a man this plan put on an empty floor five
       rooms ago was allocated that floor by nobody, and walling idle attendants out behind
       him is a decision this function has no standing to take. */
    const allocatedTo = onShift.filter(
      (lane) => allocated.get(task.floorCode)?.has(lane.userId) === true)

    const eligible = allocatedTo.length > 0 ? allocatedTo : onShift

    /* **THE SAME `bestFitFor` THE ROOM CARD USES**, handed lanes whose loads include what
       this plan has already given them. Restating the ordering here — the floor, then the
       room left, then the bigger shift — would be a second copy of CQ-141's answer, and
       the first morning they disagreed the board and the handset would recommend two
       different people for one room.

       Its floor comparison still earns its keep on an UNALLOCATED floor, where it is what
       keeps that floor together behind the man the plan first gave it to. On an allocated
       floor every candidate is on it and the comparison is settled before it runs. */
    const candidates: (FloorAwareLane & { lane: BoardLane })[] = eligible.map((lane) => {
      const running = held.get(lane.userId)!
      return {
        lane,
        rosterState: lane.rosterState,
        creditBudget: lane.creditBudget,
        /* The cards it holds plus what the plan has added, expressed as the one thing
           `workloadOf` reads — so there is still only one way to compute a workload. */
        tasks: [...lane.tasks, { credits: running.extra }],
        onThisFloor: running.floors.has(task.floorCode),
      }
    })

    /* **AND HE MUST HAVE ROOM FOR THIS ROOM, NOT MERELY ROOM** — defect #179. See
       `hasRoomFor`: `bestFitFor` answers a one-card question and a plan is charged. */
    const chosen = bestFitFor(candidates.filter(
      (c) => hasRoomFor(c.creditBudget!, sumCredits(c.tasks), task.credits)))

    if (!chosen) {
      /* **WHICH OF THE TWO REFUSALS IT IS DEPENDS ON WHETHER A WALL WAS IN THE WAY — AND
         ON WHETHER ANYBODY WAS STANDING BEHIND IT.** A floor's men being full on a board
         where nobody has an hour left is not a floor problem, and calling it one would
         send a supervisor to move a hand that does not exist. */
      const walledOut = allocatedTo.length > 0
        && onShift.some((lane) => {
          const running = held.get(lane.userId)!
          return canReceiveWork(lane.rosterState)
            && hasRoomFor(
              lane.creditBudget!,
              sumCredits([...lane.tasks, { credits: running.extra }]),
              task.credits)
        })

      unplaced.push({ task, reason: walledOut ? 'NOBODY_FREE_ON_FLOOR' : 'NOBODY_FREE' })
      continue
    }

    const running = held.get(chosen.lane.userId)!
    running.extra = sumCredits([{ credits: running.extra }, { credits: task.credits }])
    /* **AND THE FLOOR IS NOW HIS.** Two rooms on the same floor should go to the same
       person, and they only do if the first placement is allowed to influence the second.

       **IT PREFERS AND IT DOES NOT ALLOCATE** — CQ-145. This man is now `bestFitFor`'s
       first choice for the rest of that floor, which is what keeps a floor together; he
       does not thereby acquire the floor, and when he reaches his hours the next room on
       it goes to the lightest lane rather than coming back unplaced. Allocating a floor is
       something a hotel does to a man, not something a suggestion does to a hotel. */
    running.floors.add(task.floorCode)

    placements.push({
      task,
      toUserId: chosen.lane.userId,
      toLabel: chosen.lane.displayName,
      toLabelAr: chosen.lane.displayNameAr,
      onThisFloor: chosen.onThisFloor,
    })
  }

  /* **HOLDING NOTHING AT ALL**, not "holding less than somebody else". A lane with one
     room is a lane with a morning; a lane with none is a person standing in a corridor,
     and that is a fact about the plan rather than a judgement about the balance. */
  const given = new Set(placements.map((p) => p.toUserId))
  const wouldBeIdle: IdleLane[] = onShift
    .filter((lane) => lane.tasks.length === 0 && !given.has(lane.userId))
    .map((lane) => ({
      userId: lane.userId,
      label: lane.displayName,
      labelAr: lane.displayNameAr,
    }))

  return { placements, unplaced, wouldBeIdle }
}
